import { useState, useEffect } from 'react';
import { api } from '../api';

function fullName(s) { return s ? `${s.firstName || ''} ${s.lastName || ''}`.trim() : ''; }
function className(c) { return c ? `${c.name} - ${c.section}` : '—'; }

export default function Attendance() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendance, setAttendance] = useState({});  // studentId -> status
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('mark');

  useEffect(() => {
    api.getClasses().then(setClasses);
  }, []);

  useEffect(() => {
    if (!selectedClass) { setStudents([]); return; }
    api.getStudentsByClass(selectedClass).then(s => {
      setStudents(s);
      const init = {};
      s.forEach(st => init[st.id] = 'Present');
      setAttendance(init);
    });
  }, [selectedClass]);

  useEffect(() => {
    if (!selectedClass || !date) return;
    api.getAttendanceByClass(selectedClass, date).then(list => {
      const map = {};
      list.forEach(a => map[a.student.id] = a.status);
      if (list.length > 0) {
        setAttendance(prev => ({ ...prev, ...map }));
      }
    });
  }, [selectedClass, date]);

  const toggleStatus = (sid) => {
    setAttendance(prev => {
      const cur = prev[sid] || 'Present';
      const next = cur === 'Present' ? 'Absent' : cur === 'Absent' ? 'Leave' : 'Present';
      return { ...prev, [sid]: next };
    });
  };

  const setStatus = (sid, status) => {
    setAttendance(prev => ({ ...prev, [sid]: status }));
  };

  const handleSave = async () => {
    if (!selectedClass || students.length === 0) return;
    setSaving(true);
    const payload = students.map(s => ({
      student: { id: s.id },
      classSection: { id: parseInt(selectedClass) },
      attDate: date,
      status: attendance[s.id] || 'Present'
    }));
    try {
      await api.saveAttendanceBulk(payload);
      window.location.reload();
    } catch (e) { alert('Save failed: ' + e.message); }
    setSaving(false);
  };

  const presentCount = students.filter(s => (attendance[s.id] || 'Present') === 'Present').length;
  const absentCount = students.filter(s => attendance[s.id] === 'Absent').length;
  const leaveCount = students.filter(s => attendance[s.id] === 'Leave').length;

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'mark' ? ' active' : ''}`} onClick={() => setTab('mark')}>Mark Attendance</button>
        <button className={`tab${tab === 'report' ? ' active' : ''}`} onClick={() => setTab('report')}>Report</button>
      </div>

      {tab === 'mark' && (
        <div>
          <div className="card">
            <div className="card-header">
              <div className="card-title">Select Class &amp; Date</div>
            </div>
            <div className="card-body">
              <div className="form-grid cols-3">
                <div className="form-group">
                  <label className="form-label">Class</label>
                  <select className="form-control" value={selectedClass} onChange={e => setSelectedClass(e.target.value)}>
                    <option value="">-- Select Class --</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{className(c)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input className="form-control" type="date" value={date} onChange={e => setDate(e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          {students.length > 0 && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon green"><i className="fas fa-check-circle"></i></div>
                  <div>
                    <div className="stat-label">Present</div>
                    <div className="stat-value">{presentCount}</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon red"><i className="fas fa-times-circle"></i></div>
                  <div>
                    <div className="stat-label">Absent</div>
                    <div className="stat-value">{absentCount}</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon orange"><i className="fas fa-clock"></i></div>
                  <div>
                    <div className="stat-label">Leave</div>
                    <div className="stat-value">{leaveCount}</div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="card-title">Mark Attendance</div>
                  <div className="card-subtitle">Click P / A / L to set status</div>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr><th>#</th><th>Admission No</th><th>Name</th><th>Status</th><th>Mark</th></tr>
                    </thead>
                    <tbody>
                      {students.map((s, i) => {
                        const st = attendance[s.id] || 'Present';
                        return (
                          <tr key={s.id}>
                            <td>{i + 1}</td>
                            <td>{s.admissionNo}</td>
                            <td>{fullName(s)}</td>
                            <td>
                              <span className={`badge ${st === 'Present' ? 'badge-green' : st === 'Absent' ? 'badge-red' : 'badge-orange'}`}>{st}</span>
                            </td>
                            <td>
                              <div className="card-actions">
                                <button className={`btn btn-sm${st === 'Present' ? ' btn-success' : ' btn-outline'}`} onClick={() => setStatus(s.id, 'Present')}>P</button>
                                <button className={`btn btn-sm${st === 'Absent' ? ' btn-danger' : ' btn-outline'}`} onClick={() => setStatus(s.id, 'Absent')}>A</button>
                                <button className={`btn btn-sm${st === 'Leave' ? ' btn-warning' : ' btn-outline'}`} onClick={() => setStatus(s.id, 'Leave')}>L</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="form-actions">
                  <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                    <i className="fas fa-save"></i> {saving ? 'Saving…' : 'Save Attendance'}
                  </button>
                  {saved && <span className="badge badge-green">Saved successfully!</span>}
                </div>
              </div>
            </>
          )}

          {selectedClass && students.length === 0 && (
            <div className="card"><div className="card-body" style={{ color: '#888', textAlign: 'center' }}>No students in this class</div></div>
          )}
        </div>
      )}

      {tab === 'report' && <AttendanceReport classes={classes} />}
    </div>
  );
}

function AttendanceReport({ classes }) {
  const [selectedClass, setSelectedClass] = useState('');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [students, setStudents] = useState([]);
  const [report, setReport] = useState({});

  const loadReport = async () => {
    if (!selectedClass) return;
    const studs = await api.getStudentsByClass(selectedClass);
    setStudents(studs);
    const rep = {};
    for (const s of studs) {
      const att = await api.getMonthlyAttendance(s.id, month, year);
      const p = att.filter(a => a.status === 'Present').length;
      const a = att.filter(a => a.status === 'Absent').length;
      const l = att.filter(a => a.status === 'Leave').length;
      rep[s.id] = { present: p, absent: a, leave: l, total: p + a + l };
    }
    setReport(rep);
  };

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Monthly Attendance Report</div>
      </div>
      <div className="card-body">
        <div className="form-grid cols-3" style={{ marginBottom: 16 }}>
          <div className="form-group">
            <label className="form-label">Class</label>
            <select className="form-control" value={selectedClass} onChange={e => setSelectedClass(e.target.value)}>
              <option value="">-- Select Class --</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name} - {c.section}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Month</label>
            <select className="form-control" value={month} onChange={e => setMonth(parseInt(e.target.value))}>
              {months.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Year</label>
            <input className="form-control" type="number" value={year} onChange={e => setYear(parseInt(e.target.value))} />
          </div>
        </div>
        <div className="form-actions" style={{ marginBottom: 16 }}>
          <button className="btn btn-primary" onClick={loadReport}>
            <i className="fas fa-chart-bar"></i> Generate Report
          </button>
        </div>
        {students.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>#</th><th>Name</th><th>Adm No</th><th>Present</th><th>Absent</th><th>Leave</th><th>Total Days</th><th>%</th></tr>
              </thead>
              <tbody>
                {students.map((s, i) => {
                  const r = report[s.id] || { present: 0, absent: 0, leave: 0, total: 0 };
                  const pct = r.total > 0 ? ((r.present / r.total) * 100).toFixed(1) : '0.0';
                  return (
                    <tr key={s.id}>
                      <td>{i + 1}</td>
                      <td>{fullName(s)}</td>
                      <td>{s.admissionNo}</td>
                      <td><span className="badge badge-green">{r.present}</span></td>
                      <td><span className="badge badge-red">{r.absent}</span></td>
                      <td><span className="badge badge-orange">{r.leave}</span></td>
                      <td>{r.total}</td>
                      <td>
                        <span className={`badge ${parseFloat(pct) >= 75 ? 'badge-green' : 'badge-red'}`}>{pct}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
